import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de as deLocale, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import {
  compareCivilDates,
  formatCivilDate,
  isCivilDate,
  toCalendarDate,
  type CivilDate,
} from '@eis/core';

/** civil date → `Date` at local midnight, the representation the picker works in. */
const fromCivilDate = (value: CivilDate): Date => {
  const date = toCalendarDate(value);
  return new Date(date.year, date.month - 1, date.day);
};

/** `Date` (picker-local) → civil date, without ever crossing a timezone boundary. */
const toCivilDate = (value: Date): CivilDate =>
  `${String(value.getFullYear()).padStart(4, '0')}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;

export interface EffectiveDateSelectorProps {
  readonly label?: string;
  readonly value: CivilDate | null;
  readonly onChange: (value: CivilDate | null) => void;
  /** Validity window the chosen date must fall inside — e.g. a Group Policy's cover period. */
  readonly minDate?: CivilDate | null;
  readonly maxDate?: CivilDate | null;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly helperText?: string;
  /** Renders the "state as of …" banner. Detail screens that are time-travelling should set this. */
  readonly showAsOfBanner?: boolean;
  /** Custom banner content, if the default sentence is not specific enough. */
  readonly asOfBanner?: ReactNode;
  readonly locale?: string;
}

/**
 * The ONLY sanctioned entry point to MUI's pickers (CLAUDE.md rule 6 — modules are lint-banned
 * from importing `@mui/x-date-pickers` directly).
 *
 * Effective dates carry contract meaning: the date a cover starts is a **civil date**, identical
 * in every timezone. Every module reaching for a raw picker would eventually feed a `Date` into
 * domain state and reintroduce the off-by-one-day class of bug this wrapper exists to prevent.
 * Values in and out are always `YYYY-MM-DD` strings.
 */
export function EffectiveDateSelector({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  helperText,
  showAsOfBanner = false,
  asOfBanner,
  locale = 'de-DE',
}: EffectiveDateSelectorProps): ReactNode {
  const { t } = useTranslation('patterns');

  const outOfRange =
    value !== null &&
    isCivilDate(value) &&
    ((minDate != null && compareCivilDates(value, minDate) < 0) ||
      (maxDate != null && compareCivilDates(value, maxDate) > 0));

  const rangeMessage = (): string | undefined => {
    if (!outOfRange || value === null) return undefined;
    if (minDate != null && compareCivilDates(value, minDate) < 0) {
      return t('effectiveDate.beforeStart', { from: formatCivilDate(minDate, locale) });
    }
    if (maxDate != null) {
      return t('effectiveDate.afterEnd', { to: formatCivilDate(maxDate, locale) });
    }
    return undefined;
  };

  return (
    <Stack spacing={1}>
      {/*
        Without an adapter locale the picker renders MM/DD/YYYY regardless of the UI language —
        an effective date shown as 08/30/2026 to a German user is a support ticket at best.
      */}
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={locale.startsWith('de') ? deLocale : enGB}
      >
        <DatePicker
          label={label ?? t('effectiveDate.label')}
          value={value === null ? null : fromCivilDate(value)}
          onChange={(next) => {
            if (next === null) {
              onChange(null);
              return;
            }
            // While a date is being TYPED, the picker reports every intermediate state — and an
            // incomplete one arrives as an Invalid Date, not as null. Converting it would emit
            // "NaN-NaN-NaN", which is not a civil date, and this component's whole contract is
            // that what comes out of it is. Callers then format it and crash.
            //
            // Held rather than forwarded as null: the previous value stays until a complete date
            // exists, so a screen driven by this does not flicker back to its default on every
            // keystroke.
            if (Number.isNaN(next.getTime())) return;
            // Read the picker's *local* calendar fields. Going through an instant
            // (toISOString) would shift the day west of UTC — the exact bug the civil-date
            // layer exists to keep out of contract data.
            onChange(toCivilDate(next));
          }}
          minDate={minDate == null ? undefined : fromCivilDate(minDate)}
          maxDate={maxDate == null ? undefined : fromCivilDate(maxDate)}
          disabled={disabled}
          slotProps={{
            textField: {
              // The console's density default (CLAUDE.md rule 4): the same height as the small
              // selects and fields it sits beside.
              size: 'small',
              required,
              error: outOfRange,
              helperText: rangeMessage() ?? helperText,
              fullWidth: true,
            },
          }}
        />
      </LocalizationProvider>

      {showAsOfBanner && value !== null && (
        <Alert severity="info" icon={false}>
          {asOfBanner ?? t('effectiveDate.asOf', { date: formatCivilDate(value, locale) })}
        </Alert>
      )}
    </Stack>
  );
}
